/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Internal dependencies
import useStyleSystem from '@rakuten/hooks/useStyleSystem';
import { View } from '@rakuten/modules-core-components';
import Icon from './../atoms/Icon';
import Label, { TYPE as LABEL_TYPE } from './../atoms/Label';


// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------

/**
 * Calculate styles for this component, optionally based on props, theme,
 * currently matching breakpoint etc.
 *
 * @param {object} args Arguments used for style calculation
 * @param {object} args.theme Current theme object
 * @return {object} Object that maps style names to style objects
 */
function createStyles({ theme }) {
  const { variables, mixins } = theme;

  return {
    title: {
      alignItems: 'center',
      display: 'flex',
      height: mixins.basicUnit(4),

      ':hover': {
        cursor: 'pointer'
      }
    },

    text: {
      ...mixins.scrollable('y'),
      ...mixins.userSelect('text'),

      height: 0,
      maxHeight: 0,
      paddingLeft: mixins.basicUnit(),
      paddingRight: mixins.basicUnit(),
      transition: `height ${variables.animationDuration} ${variables.animationEasing}, max-height ${variables.animationDuration} ${variables.animationEasing}`,
      width: '100%',
      wordBreak: 'break-word'
    }
  };
}


// -----------------------------------------------------------------------------
// DISCLOSURE COMPONENT
// -----------------------------------------------------------------------------

function Disclosure(props) {
  // Initialize state variables
  const [ isExpanded, setIsExpanded ] = useState(props.isExpanded);

  // "Hook" this component into the theme/style system
  const [ styles ] = useStyleSystem(
    {
      id: 'Disclosure',
      props,
      styleFn: createStyles
    }
  );


  // ---------------------------------------------------------------------------
  // EVENT HANDLER
  // ---------------------------------------------------------------------------

  function onToggle(/* e */) {
    setIsExpanded(!isExpanded);
  }


  // ---------------------------------------------------------------------------
  // HOOKS
  // ---------------------------------------------------------------------------

  useEffect(function expandedChangeEffect() {
    if (props.isExpanded && props.isExpanded !== isExpanded) {
      setIsExpanded(props.isExpanded);
    }
  }, [ props.isExpanded ]);


  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const heightStyle = isExpanded
    ? { height : 'fit-content', maxHeight: props.maxHeight }
    : { };

  return (
    <>
      <View
        style={ styles.title }
        onPress={ onToggle }
      >
        <Label
          text={ props.title }
          type={ props.titleType }
        />
        <Icon
          icon={ isExpanded ? "arrowhead-down-small" : "arrowhead-right-small" }
        />
      </View>
      <View style={ [ styles.text, heightStyle ] }>
        { props.children }
      </View>
    </>
  );
}


// -----------------------------------------------------------------------------
// PROPS VALIDATION
// -----------------------------------------------------------------------------

Disclosure.propTypes = {
  /* Whether to show the children [optional] */
  isExpanded: PropTypes.bool,

  /* The maximum height of the expanded text [optional] */
  maxHeight: PropTypes.string,

  /* The text that is clickable to expand the component */
  title: PropTypes.string.isRequired,

  /* The type of the title label (see Label.TYPE) [optional] */
  titleType: PropTypes.string
};

Disclosure.defaultProps = {
  isExpanded: false,
  maxHeight: '25vh',
  titleType: LABEL_TYPE.TEXTSMALL
};


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default Disclosure;
